import type { AvatarArt } from '@pixelworld/game-core/client';
import {
  AVATAR_PRICES,
  CUSTOM_FIGURE,
  isShopItem,
  LOOK_CHOICES,
  LOOK_PARTS,
  type Avatar,
  type AvatarItemId,
  type LookPart,
  type Wardrobe,
} from '@pixelworld/shared';
import { useState } from 'react';
import { Button } from '../../components/Button';
import { FormError } from '../../components/Field';
import { da } from '../../i18n/da';
import { AvatarCanvas } from './AvatarCanvas';
import { AVATAR_CHOICES, firstToBuy, isChanged, isOwned, STARTING_AVATAR } from './avatar-choices';
import { buyItem, wearAvatar } from './profile-api';
import styles from './ProfilePage.module.css';

interface AvatarEditorProps {
  art: AvatarArt;
  wardrobe: Wardrobe;
  balance: number;
  onSaved: (wardrobe: Wardrobe, balance?: number) => void;
}

/** Thumbnails are the player's own figure with one thing swapped, three times its size. */
const THUMB = { width: 48, height: 84 } as const;

/**
 * The figure and the avatar shop (2026-10-26): everything can be tried on, and what is not owned
 * yet is bought here for PixelCoins, the only place it is sold. The custom figure's skin, hair,
 * eyes and mouth are free. Thumbnails and the preview are drawn by the game's own art, so new
 * drawings show up here without changes.
 */
export function AvatarEditor({ art, wardrobe, balance, onSaved }: AvatarEditorProps) {
  const [choice, setChoice] = useState<Avatar>(() => wardrobe.avatar ?? STARTING_AVATAR);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const choices = AVATAR_CHOICES;
  const toBuy = firstToBuy(wardrobe.owned, choice);

  const choose = (next: Avatar) => {
    setChoice(next);
    setSaved(null);
    setError(null);
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    const result = await wearAvatar({ avatar: choice });
    setBusy(false);
    if (!result.ok) {
      setError(da.authErrors[result.error]);
      return;
    }
    setSaved(da.profile.figureSaved);
    onSaved(result.data.wardrobe);
  };

  /** Buys the next thing tried on; once all of it is owned, it is put on right away. */
  const buy = async (item: NonNullable<typeof toBuy>) => {
    setBusy(true);
    setError(null);
    const bought = await buyItem({ item });
    if (!bought.ok) {
      setBusy(false);
      setError(da.authErrors[bought.error]);
      return;
    }
    const { wardrobe: next, balance: left } = bought.data;
    onSaved(next, left);
    if (firstToBuy(next.owned, choice) !== null) {
      setBusy(false);
      setSaved(da.profile.bought(art.itemName(item)));
      return;
    }
    const worn = await wearAvatar({ avatar: choice });
    setBusy(false);
    if (worn.ok) onSaved(worn.data.wardrobe, left);
    setSaved(da.profile.bought(art.itemName(item)));
  };

  const thumbs = <T extends AvatarItemId | null>(
    options: readonly T[],
    current: T,
    wear: (option: T) => Avatar,
    none: string,
  ) =>
    options.map((option) => {
      const price =
        option && !isOwned(wardrobe.owned, option) && isShopItem(option)
          ? AVATAR_PRICES[option]
          : null;
      const name = option ? art.itemName(option) : none;
      return (
        <button
          key={option ?? 'none'}
          type="button"
          className={styles.thumb}
          aria-pressed={option === current}
          aria-label={price === null ? name : `${name}, ${da.profile.price(price)}`}
          title={name}
          onClick={() => choose(wear(option))}
        >
          {option ? (
            <AvatarCanvas art={art} avatar={wear(option)} {...THUMB} />
          ) : (
            <span className={styles.none}>{none}</span>
          )}
          {price !== null && <span className={styles.priceTag}>{price}</span>}
        </button>
      );
    });

  const custom = choice.figure === CUSTOM_FIGURE;
  const worn = [choice.figure, choice.hat, choice.face]
    .filter((item): item is AvatarItemId => item !== null)
    .map((item) => art.itemName(item))
    .join(' · ');

  return (
    <section className={styles.section} aria-labelledby="figure-title">
      <div className={styles.figureHeader}>
        <h3 id="figure-title" className={styles.sectionTitle}>
          {da.profile.figureTitle}
        </h3>
        <span className={styles.balance} data-testid="profile-balance">
          🪙 {da.profile.balance(balance)}
        </span>
      </div>

      <div className={styles.preview}>
        <AvatarCanvas
          art={art}
          avatar={choice}
          width={96}
          height={168}
          label={da.profile.preview}
        />
        <p className={styles.worn} data-testid="avatar-worn">
          {worn}
        </p>
      </div>

      <fieldset className={styles.group}>
        <legend>{da.profile.figure}</legend>
        <div className={styles.thumbs}>
          {thumbs(choices.figures, choice.figure, (figure) => ({ ...choice, figure }), '')}
        </div>
      </fieldset>

      {custom && (
        <fieldset className={styles.group}>
          <legend>{da.profile.look}</legend>
          {LOOK_PARTS.map((part) => (
            <LookRow
              key={part}
              art={art}
              part={part}
              value={choice.look[part]}
              onChange={(value) => choose({ ...choice, look: { ...choice.look, [part]: value } })}
            />
          ))}
        </fieldset>
      )}

      <fieldset className={styles.group}>
        <legend>{da.profile.hat}</legend>
        <div className={styles.thumbs}>
          {thumbs(choices.hats, choice.hat, (hat) => ({ ...choice, hat }), da.profile.noHat)}
        </div>
      </fieldset>

      <fieldset className={styles.group}>
        <legend>{da.profile.face}</legend>
        <div className={styles.thumbs}>
          {thumbs(choices.faces, choice.face, (face) => ({ ...choice, face }), da.profile.noFace)}
        </div>
      </fieldset>

      <p className={styles.hint}>{da.profile.shopHint}</p>
      <FormError message={error} />
      {saved && (
        <p className={styles.saved} role="status">
          {saved}
        </p>
      )}
      {toBuy ? (
        <>
          {AVATAR_PRICES[toBuy] > balance && (
            <p className={styles.hint}>{da.profile.missing(AVATAR_PRICES[toBuy] - balance)}</p>
          )}
          <Button disabled={busy || AVATAR_PRICES[toBuy] > balance} onClick={() => void buy(toBuy)}>
            {da.profile.buy(art.itemName(toBuy), AVATAR_PRICES[toBuy])}
          </Button>
        </>
      ) : (
        <Button disabled={busy || !isChanged(wardrobe, choice)} onClick={() => void save()}>
          {da.profile.saveFigure}
        </Button>
      )}
    </section>
  );
}

interface LookRowProps {
  art: AvatarArt;
  part: LookPart;
  value: string;
  onChange: (value: string) => void;
}

/** One part of the custom figure, as a row of named choices. */
function LookRow({ art, part, value, onChange }: LookRowProps) {
  const choices: readonly string[] = LOOK_CHOICES[part];
  return (
    <div className={styles.lookRow} role="group" aria-label={art.lookPartName(part)}>
      <span className={styles.lookLabel}>{art.lookPartName(part)}</span>
      <div className={styles.chips}>
        {choices.map((choice) => (
          <button
            key={choice}
            type="button"
            className={styles.chip}
            aria-pressed={choice === value}
            onClick={() => onChange(choice)}
          >
            {art.lookChoiceName(part, choice)}
          </button>
        ))}
      </div>
    </div>
  );
}
